import { useEffect, useLayoutEffect, useRef, useState } from "react";

export interface FilterOption {
  value: string;
  count: number;
}

export interface FilterOptionGroup {
  key: string;
  label: string;
  options: FilterOption[];
}

type Props = {
  open: boolean;
  anchor: HTMLElement | null;
  groups: FilterOptionGroup[];
  initialValues: Record<string, string[]>;
  onApply: (next: Record<string, string[]>) => void;
  onClose: () => void;
};

type PopPos = {
  top: number;
  left: number;
  width: number;
  maxHeight: number;
};

const VIEWPORT_MARGIN = 12;
const PREFERRED_WIDTH = 300;
const MIN_HEIGHT = 200;

export function FilterPopover({
  open,
  anchor,
  groups,
  initialValues,
  onApply,
  onClose,
}: Props) {
  const popRef = useRef<HTMLDivElement | null>(null);
  const [values, setValues] = useState<Record<string, string[]>>(initialValues);
  const [pos, setPos] = useState<PopPos | null>(null);

  useEffect(() => {
    if (open) setValues(initialValues);
  }, [open, initialValues]);

  // Position + size the popover relative to its trigger while keeping it
  // inside the viewport. Re-runs on open, scroll, and resize so the popover
  // never gets cut off on smaller screens.
  useLayoutEffect(() => {
    if (!open || !anchor) return;

    const compute = () => {
      const rect = anchor.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      const width = Math.min(PREFERRED_WIDTH, vw - VIEWPORT_MARGIN * 2);

      let left = rect.left;
      if (left + width > vw - VIEWPORT_MARGIN) {
        left = Math.max(VIEWPORT_MARGIN, vw - width - VIEWPORT_MARGIN);
      }
      if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN;

      const spaceBelow = vh - rect.bottom - VIEWPORT_MARGIN - 8;
      const spaceAbove = rect.top - VIEWPORT_MARGIN - 8;
      const openUpward = spaceBelow < MIN_HEIGHT && spaceAbove > spaceBelow;

      const top = openUpward
        ? Math.max(VIEWPORT_MARGIN, rect.top - 8 - Math.min(spaceAbove, vh * 0.7))
        : rect.bottom + 8;

      const maxHeight = openUpward
        ? Math.max(MIN_HEIGHT, Math.min(spaceAbove, vh * 0.7))
        : Math.max(MIN_HEIGHT, Math.min(spaceBelow, vh * 0.7));

      setPos({ top, left, width, maxHeight });
    };

    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
    };
  }, [open, anchor]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current && popRef.current.contains(t)) return;
      if (anchor && anchor.contains(t)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, anchor, onClose]);

  if (!open || !pos) return null;

  const toggleValue = (key: string, value: string) => {
    setValues((prev) => {
      const next = { ...prev };
      const current = next[key] ?? [];
      if (current.includes(value)) {
        const remaining = current.filter((v) => v !== value);
        if (remaining.length === 0) delete next[key];
        else next[key] = remaining;
      } else {
        next[key] = [...current, value];
      }
      return next;
    });
  };

  const clearGroup = (key: string) => {
    setValues((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const reset = () => setValues({});

  const apply = () => {
    onApply(values);
    onClose();
  };

  const totalSelected = Object.values(values).reduce(
    (sum, arr) => sum + (arr?.length ?? 0),
    0
  );

  return (
    <div
      ref={popRef}
      className="filter-popover"
      role="dialog"
      aria-label="Filters"
      style={{
        top: pos.top,
        left: pos.left,
        width: pos.width,
        maxHeight: pos.maxHeight,
      }}
    >
      <div className="filter-popover-body">
        {groups.length === 0 ? (
          <p className="filter-popover-empty">No filter options available.</p>
        ) : (
          groups.map((g) => {
            const selected = values[g.key] ?? [];
            return (
              <div className="filter-popover-group" key={g.key}>
                <div className="filter-popover-group-head">
                  <div className="filter-popover-group-title">{g.label}</div>
                  {selected.length > 0 ? (
                    <button
                      type="button"
                      className="filter-popover-group-clear"
                      onClick={() => clearGroup(g.key)}
                    >
                      Clear
                    </button>
                  ) : null}
                </div>
                <div className="filter-popover-options">
                  {g.options.length === 0 ? (
                    <p className="filter-popover-empty">No values</p>
                  ) : (
                    g.options.map((opt) => {
                      const checked = selected.includes(opt.value);
                      return (
                        <label
                          key={opt.value}
                          className={`filter-popover-option${
                            checked ? " is-checked" : ""
                          }`}
                          title={opt.value}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleValue(g.key, opt.value)}
                          />
                          <span className="filter-popover-option-label">
                            {opt.value}
                          </span>
                          <span className="filter-popover-option-count">
                            {opt.count}
                          </span>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
      <div className="filter-popover-foot">
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={reset}
          disabled={totalSelected === 0}
        >
          Reset
        </button>
        <div className="filter-popover-actions">
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={apply}
          >
            Apply{totalSelected > 0 ? ` (${totalSelected})` : ""}
          </button>
        </div>
      </div>
    </div>
  );
}
